import type {
  Production,
  ProductionFormErrors,
  ProductionFormValues,
  ProductionInput,
} from '../types/production';
import { isHttpUrl } from './production';

export const emptyFormValues: ProductionFormValues = {
  title: '',
  synopsis: '',
  category: '',
  year: '',
  durationMinutes: '',
  posterUrl: '',
  videoUrl: '',
  collection: '',
  isOriginal: true,
  isFeatured: false,
  status: 'draft',
  releaseDate: '',
};

export function valuesFromProduction(p: Production): ProductionFormValues {
  return {
    title: p.title,
    synopsis: p.synopsis ?? '',
    category: p.category ?? '',
    year: p.year ? String(p.year) : '',
    durationMinutes: p.durationMinutes ? String(p.durationMinutes) : '',
    posterUrl: p.posterUrl ?? '',
    videoUrl: p.videoUrl ?? '',
    collection: p.collection ?? '',
    isOriginal: p.isOriginal,
    isFeatured: p.isFeatured,
    status: p.status,
    releaseDate: p.releaseDate ?? '',
  };
}

export function valuesToInput(v: ProductionFormValues): ProductionInput {
  return {
    title: v.title.trim(),
    synopsis: v.synopsis.trim() || null,
    category: v.category || null,
    year: v.year ? Number(v.year) : null,
    durationMinutes: v.durationMinutes ? Number(v.durationMinutes) : null,
    posterUrl: v.posterUrl.trim() || null,
    videoUrl: v.videoUrl.trim() || null,
    collection: v.collection.trim() || null,
    isOriginal: v.isOriginal,
    isFeatured: v.isFeatured,
    status: v.status,
    releaseDate: v.status === 'scheduled' ? v.releaseDate : null,
  };
}

export function buildPreviewProduction(v: ProductionFormValues): Production {
  return {
    ...valuesToInput(v),
    title: v.title.trim() || 'Sin título',
    id: 'vista-previa',
    slug: 'vista-previa',
    isDemo: false,
    createdAt: new Date().toISOString(),
  };
}

export function validateFormValues(
  v: ProductionFormValues,
  now: Date = new Date(),
): ProductionFormErrors {
  const errors: ProductionFormErrors = {};
  const maxYear = now.getFullYear() + 2;

  const title = v.title.trim();
  if (!title) errors.title = 'El título es obligatorio.';
  else if (title.length > 120) errors.title = 'El título admite un máximo de 120 caracteres.';

  if (v.synopsis.trim().length > 600) errors.synopsis = 'La descripción admite un máximo de 600 caracteres.';

  if (v.year) {
    const year = Number(v.year);
    if (!Number.isInteger(year) || year < 1900 || year > maxYear) {
      errors.year = `Introduce un año entre 1900 y ${maxYear}.`;
    }
  }

  if (v.durationMinutes) {
    const minutes = Number(v.durationMinutes);
    if (!Number.isInteger(minutes) || minutes < 1 || minutes > 600) {
      errors.durationMinutes = 'Introduce la duración en minutos (entre 1 y 600).';
    }
  }

  if (v.posterUrl.trim() && !isHttpUrl(v.posterUrl.trim())) {
    errors.posterUrl = 'Introduce una URL válida que empiece por https://';
  }

  if (v.videoUrl.trim() && !isHttpUrl(v.videoUrl.trim())) {
    errors.videoUrl = 'Introduce una URL válida que empiece por https://';
  }

  if (v.collection.trim().length > 60) errors.collection = 'Máximo 60 caracteres.';

  if (v.status === 'scheduled') {
    if (!v.releaseDate) {
      errors.releaseDate = 'Indica la fecha de estreno.';
    } else {
      const release = new Date(`${v.releaseDate}T00:00:00`);
      if (Number.isNaN(release.getTime()) || release <= now) {
        errors.releaseDate = 'La fecha de estreno debe ser posterior a hoy.';
      }
    }
  }

  return errors;
}
